import React, { useState, useEffect } from 'react';
import { pbEngineer } from '../lib/pb';
import { useToast } from '../shared/components/ToastProvider';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import { Icons } from '../shared/icons';
import SafeIcon from '../common/SafeIcon';

const EXPENSE_TYPES = ['mileage', 'fuel', 'toll', 'other'];
const EXPENSE_LABELS = { mileage: 'Mileage', fuel: 'Fuel', toll: 'Tolls', other: 'Other' };

// Expense capture (spec §3) — entry starts now, office reporting comes
// later. Engineers create their own rows (createRule: engineer own OR
// agency); read-back is own rows only.
export default function EngineerExpenses() {
  const { notify } = useToast();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [type, setType] = useState('mileage');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const engineer = pbEngineer.authStore.record;

  const load = async () => {
    const engineerId = engineer?.id;
    if (!engineerId) return;
    try {
      const records = await pbEngineer.collection('expenses').getFullList({
        filter: `engineer_id="${engineerId}"`,
        sort: '-date,-created',
      });
      setExpenses(records);
    } catch (err) {
      console.error('Failed to load expenses:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async () => {
    const value = Number(amount);
    if (!value || value <= 0) {
      notify('Enter a valid amount', 'error');
      return;
    }
    if (!date) {
      notify('Pick a date', 'error');
      return;
    }
    setSaving(true);
    try {
      await pbEngineer.collection('expenses').create({
        tenant_id: engineer?.tenant_id,
        engineer_id: engineer.id,
        type,
        amount: value,
        date,
        notes: notes.trim(),
      });
      notify('Expense logged', 'success');
      setAmount('');
      setNotes('');
      await load();
    } catch (err) {
      console.error('Failed to log expense:', err);
      notify(err.message || 'Could not log the expense', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-container"><Skeleton rows={6} /></div>;

  const total = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Expenses</h1>
        <p>Log mileage, fuel, tolls and other out-of-pocket costs</p>
      </div>

      <div className="expenses-layout">
        <div className="expense-form">
          <h2>Log an expense</h2>
          <div className="form-field">
            <label htmlFor="expense-type">Type</label>
            <select id="expense-type" value={type} onChange={(e) => setType(e.target.value)}>
              {EXPENSE_TYPES.map((t) => (
                <option key={t} value={t}>{EXPENSE_LABELS[t]}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="expense-amount">Amount (£)</label>
            <input
              id="expense-amount"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label htmlFor="expense-date">Date</label>
            <input
              id="expense-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label htmlFor="expense-notes">Notes</label>
            <textarea
              id="expense-notes"
              placeholder={type === 'mileage' ? 'e.g. Guildford → Woking, 24 miles' : 'e.g. Dartford crossing'}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <button className="primary-button" onClick={handleSubmit} disabled={saving}>
            <SafeIcon icon={Icons.Plus} size={15} />
            {saving ? 'Saving…' : 'Log Expense'}
          </button>
        </div>

        <div>
          {expenses.length === 0 ? (
            <EmptyState
              title="No expenses logged"
              message="Your mileage, fuel, tolls and other costs will appear here."
              icon={Icons.Cash}
            />
          ) : (
            <div className="expense-list">
              {expenses.map((expense) => (
                <div key={expense.id} className="expense-item">
                  <span className="expense-type">{EXPENSE_LABELS[expense.type] || expense.type}</span>
                  <div>
                    <div className="expense-note">{expense.notes || expense.date || ''}</div>
                    <div className="expense-note">
                      {new Date(expense.date || expense.created).toLocaleDateString()}
                    </div>
                  </div>
                  <span className="expense-amount">£{Number(expense.amount).toFixed(2)}</span>
                </div>
              ))}
              <div className="expense-item" style={{ background: '#f2f8f5', borderColor: '#bdd0c9' }}>
                <strong>Total</strong>
                <span className="expense-amount">£{total.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
